import { useState, useEffect, useCallback } from 'react';

// "Bring me back to the card I just clicked" + "show me which one it was".
//
// On click, the page calls `saveLastVisited(id)` to record the ID into
// sessionStorage. On the back-navigation remount, once `isReady` flips
// true, we look up the card by its `data-property-id` attribute and
// scrollIntoView it (block: center). The matching ID is also exposed as
// `highlightedId` for ~2.2s so the page can ring/glow that card as a
// visual cue, then it auto-clears.
//
// Why ID-based and not scrollTop: card lists frequently grow taller as
// images decode and lazy rows hydrate. A saved scroll offset gets clipped
// when the page is shorter at restore time and never recovers. ID-based
// restoration is layout-agnostic — `scrollIntoView` always lands on the
// element regardless of its position or page height at the moment.
//
// `key` is a per-page sessionStorage key, e.g. 'intercambio-prop:lastVisited'.
export function useLastVisitedRestore(
  key: string,
  isReady: boolean,
): {
  highlightedId: string | null;
  saveLastVisited: (id: string) => void;
} {
  const [highlightedId, setHighlightedId] = useState<string | null>(null);

  useEffect(() => {
    if (!isReady) return;
    let lastId: string | null = null;
    try {
      lastId = sessionStorage.getItem(key);
    } catch {
      return;
    }
    if (!lastId) return;

    // No "restored once" ref guard here on purpose. React 18 StrictMode (dev
    // only) invokes effects twice; a ref-based guard set on the first run
    // sticks through the cleanup, causing the second run to bail and miss
    // the restoration entirely. Relying on the deps array + the cleanup
    // function to cancel in-flight retries is enough to avoid double work
    // in production while keeping StrictMode happy.
    let attempt = 0;
    let timer: number | null = null;
    let raf1: number | null = null;
    let raf2: number | null = null;
    let cancelled = false;

    const trySelect = () => {
      if (cancelled) return;
      const el = document.querySelector<HTMLElement>(
        `[data-property-id="${(window as any).CSS && CSS.escape ? CSS.escape(lastId!) : lastId}"]`,
      );
      if (el) {
        el.scrollIntoView({ block: 'center', behavior: 'auto' });
        setHighlightedId(lastId);
        return;
      }
      attempt++;
      if (attempt >= 30) return; // ~3s, give up silently
      timer = window.setTimeout(trySelect, 100);
    };

    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(trySelect);
    });

    return () => {
      cancelled = true;
      if (raf1 != null) cancelAnimationFrame(raf1);
      if (raf2 != null) cancelAnimationFrame(raf2);
      if (timer != null) clearTimeout(timer);
    };
  }, [key, isReady]);

  // Auto-clear the highlight after a few seconds so the cue fades on its
  // own without the page having to manage a timer.
  useEffect(() => {
    if (!highlightedId) return;
    const t = setTimeout(() => setHighlightedId(null), 2200);
    return () => clearTimeout(t);
  }, [highlightedId]);

  const saveLastVisited = useCallback(
    (id: string) => {
      try {
        sessionStorage.setItem(key, id);
      } catch {
        // ignore
      }
    },
    [key],
  );

  return { highlightedId, saveLastVisited };
}
